import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaCamera, FaArrowLeft, FaCheck, FaPlus, FaEnvelope, FaUserShield } from 'react-icons/fa';
import { getTokenFromLocalStorage } from '../../utilities/tokenUtils';
import PasswordInput from '../../components/PasswordInput';
import { colors, font } from './partnerTheme';
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL || '';
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'lusuku-clou';

// ── Static styles (module-scope — NOT inside the component) ─────────────────────────────
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: '#6B7280', marginBottom: 5,
  fontFamily: font.sans,
};
const inputBase = {
  width: '100%', padding: '9px 12px', fontSize: 14,
  border: '1px solid #E5E7EB', borderRadius: 8, outline: 'none',
  background: '#FAFAFA', color: '#111', transition: 'border-color 0.15s, background 0.15s',
  boxSizing: 'border-box',
  fontFamily: font.sans,
};
const disabledInput = { ...inputBase, background: '#F3F4F6', color: '#9CA3AF', cursor: 'not-allowed' };
const sectionTitle = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
  color: '#9CA3AF', margin: '0 0 20px',
  fontFamily: font.mono,
};
const card = {
  background: '#fff', borderRadius: 14, border: '1px solid #E5E7EB',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 16,
};
const cardBody = { padding: '24px 28px' };
const grid = (cols) => ({ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '0 20px' });
const onFocus = e => {
  e.target.style.borderColor = '#006202';
  e.target.style.background = '#fff';
  e.target.style.boxShadow = '0 0 0 3px rgba(0,98,2,0.08)';
};
const onBlur = e => {
  e.target.style.borderColor = '#E5E7EB';
  e.target.style.background = '#FAFAFA';
  e.target.style.boxShadow = 'none';
};
const inp = (extra = {}) => ({ style: { ...inputBase, ...extra }, onFocus, onBlur });
const sel = () => ({ style: { ...inputBase }, onFocus, onBlur });

/** Field wrapper — defined at MODULE SCOPE so React never sees a new type per render. */
const Field = ({ label, required: req, help, children }) => (
  <div>
    <label style={labelStyle}>
      {label}{req && <span style={{ color: '#EF4444', marginLeft: 2 }}>*</span>}
    </label>
    {children}
    {help && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9CA3AF' }}>{help}</p>}
  </div>
);


const CreatePartner = () => {
  const history = useHistory();
  const { id } = useParams(); // Get partner ID from URL if editing
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tierConfig, setTierConfig] = useState(null);
  const [tierConfigError, setTierConfigError] = useState(null);
  const [useCustomRates, setUseCustomRates] = useState(false);

  // Logo state (edit mode only)
  const [currentLogo, setCurrentLogo] = useState(null);  // { url, publicId }
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [cloudinaryLoaded, setCloudinaryLoaded] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '',
    registrationNumber: '',
    taxId: '',
    status: 'pending', // pending, active, suspended, inactive
    contactPerson: {
      name: '',
      phone: '',
      email: '',
      position: '',
    },
    address: {
      street: '',
      city: '',
      district: '',
      country: 'Uganda',
    },
    commissionRate: 40,
    partnerShareRate: 18,
    riderShareRate: 42,
    baseDeliveryFee: 5000,
    partnershipTier: 'bronze',
    bankDetails: {
      bankName: '',
      accountName: '',
      accountNumber: '',
      branchCode: '',
    },
    // Login email (partner will set own password via invite email)
    email: '',
    // password only used in edit mode to optionally reset
    password: '',
    confirmPassword: '',
  });

  // Fetch tier configuration on mount
  useEffect(() => {
    fetchTierConfig();
  }, []);

  // Load Cloudinary widget script
  useEffect(() => {
    if (window.cloudinary) { setCloudinaryLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://widget.cloudinary.com/v2.0/global/all.js';
    script.async = true;
    script.onload = () => setCloudinaryLoaded(true);
    document.body.appendChild(script);
  }, []);

  // Fetch existing partner data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      fetchPartnerData();
    }
  }, [id, isEditMode]);

  const fetchTierConfig = async () => {
    try {
      const token = getTokenFromLocalStorage();
      const response = await axios.get(`${API_URL}/api/3pl-partners/tier-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setTierConfig(response.data.data);
        // Set initial values from bronze tier
        const bronzeConfig = response.data.data.bronze;
        if (bronzeConfig && !isEditMode) {
          setFormData(prev => ({
            ...prev,
            commissionRate: bronzeConfig.commissionRate,
            partnerShareRate: bronzeConfig.partnerShareRate,
            riderShareRate: bronzeConfig.riderShareRate,
            baseDeliveryFee: bronzeConfig.baseDeliveryFee,
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching tier config:', err);
      // Silence here is what made a tier change label-only: the dropdown still
      // moved, the rate boxes did not, and the save looked fine. The server now
      // derives the rates itself so the save is still correct, but the admin
      // must know the numbers on screen are not the ones being stored.
      setTierConfigError(
        'Tier rates could not be loaded, so the percentages shown may be out of date. Saving still applies the correct rates for the tier you pick.'
      );
    }
  };

  const fetchPartnerData = async () => {
    try {
      setFetchingData(true);
      const token = getTokenFromLocalStorage();
      const response = await axios.get(`${API_URL}/api/3pl-partners/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const partnerData = response.data.data?.partner || response.data.data;

      if (partnerData) {
        setCurrentLogo(partnerData.logo || null);
        setFormData({
          companyName: partnerData.companyName || '',
          registrationNumber: partnerData.registrationNumber || '',
          taxId: partnerData.taxId || '',
          status: partnerData.status || 'pending',
          contactPerson: {
            name: partnerData.contactPerson?.name || '',
            phone: partnerData.contactPerson?.phone || '',
            email: partnerData.contactPerson?.email || '',
            position: partnerData.contactPerson?.position || '',
          },
          address: {
            street: partnerData.address?.street || '',
            city: partnerData.address?.city || '',
            district: partnerData.address?.district || '',
            country: partnerData.address?.country || 'Uganda',
          },
          commissionRate: partnerData.commissionRate || 40,
          partnerShareRate: partnerData.partnerShareRate || 18,
          riderShareRate: partnerData.riderShareRate || 42,
          baseDeliveryFee: partnerData.baseDeliveryFee || 5000,
          partnershipTier: partnerData.partnershipTier || 'bronze',
          bankDetails: {
            bankName: partnerData.bankDetails?.bankName || '',
            accountName: partnerData.bankDetails?.accountName || '',
            accountNumber: partnerData.bankDetails?.accountNumber || '',
            branchCode: partnerData.bankDetails?.branchCode || '',
          },
          // Load email but NOT password (security: never expose password)
          email: partnerData.email || '',
          password: '', // Always empty in edit mode
          confirmPassword: '',
        });
      }
    } catch (err) {
      setError('Failed to load partner data');
      console.error('Error fetching partner:', err);
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else if (name === 'partnershipTier' && tierConfig) {
      // Auto-fill commission rates when tier changes
      const newTierConfig = tierConfig[value];
      if (newTierConfig) {
        setFormData((prev) => ({
          ...prev,
          partnershipTier: value,
          commissionRate: newTierConfig.commissionRate,
          partnerShareRate: newTierConfig.partnerShareRate,
          riderShareRate: newTierConfig.riderShareRate,
          baseDeliveryFee: newTierConfig.baseDeliveryFee,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // ── Logo upload (admin, edit mode only) ─────────────────────────────────────
  const handleLogoUpload = () => {
    if (!cloudinaryLoaded || !window.cloudinary) {
      toast.warn('Image uploader is loading, please try again in a moment.');
      return;
    }
    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUDINARY_CLOUD_NAME,
        uploadPreset: CLOUDINARY_UPLOAD_PRESET,
        folder: `3pl-partners/${id}`,
        maxFileSize: 500000,
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp'],
        multiple: false,
        cropping: true,
        croppingAspectRatio: 1,
        croppingShowDimensions: true,
        transformation: [
          { width: 100, height: 100, crop: 'fill', gravity: 'face' },
          { quality: 'auto:good' },
        ],
        sources: ['local', 'url', 'camera'],
        showPoweredBy: false,
        styles: {
          palette: {
            window: '#FFFFFF', windowBorder: colors.blue,
            tabIcon: colors.blue, menuIcons: colors.blue,
            textDark: '#000000', textLight: '#FFFFFF',
            link: colors.blue, action: colors.green,
            inProgress: colors.blue, complete: colors.green,
            error: '#F44336', sourceBg: '#F5F5F5',
          },
        },
      },
      async (error, result) => {
        if (error) { toast.error('Upload failed. Please try again.'); return; }
        if (!result || result.event !== 'success') return;
        const { secure_url, public_id } = result.info;
        setUploadingLogo(true);
        try {
          const token = getTokenFromLocalStorage();
          await axios.put(
            `${API_URL}/api/3pl-partners/${id}`,
            { logo: { url: secure_url, publicId: public_id } },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCurrentLogo({ url: secure_url, publicId: public_id });
          toast.success('Partner logo updated successfully!');
        } catch (err) {
          toast.error(err.response?.data?.error?.message || 'Failed to save logo.');
        } finally {
          setUploadingLogo(false);
        }
      }
    );
    widget.open();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate custom commission splits sum to 100%
    if (useCustomRates) {
      const splitSum = Number(formData.commissionRate) + Number(formData.partnerShareRate) + Number(formData.riderShareRate);
      if (Math.abs(splitSum - 100) > 0.01) {
        setError(`Commission splits must sum to 100% (currently ${splitSum.toFixed(1)}%). Adjust Lusuku %, Partner %, or Rider %.`);
        return;
      }
    }

    // Password validation only in edit mode (when admin is resetting)
    if (isEditMode && (formData.password || formData.confirmPassword)) {
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long');
        return;
      }
      if (!/[A-Z]/.test(formData.password)) {
        setError('Password must contain at least one uppercase letter');
        return;
      }
      if (!/[0-9]/.test(formData.password)) {
        setError('Password must contain at least one number');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid login email address');
      return;
    }

    setLoading(true);

    try {
      const token = getTokenFromLocalStorage();
      const submitData = { ...formData };
      delete submitData.confirmPassword;
      // Tell the server whether these rates are a deliberate arrangement or
      // just the tier's. Without it the server cannot tell a custom partner
      // from a drifted one, so it now re-derives from the tier unless this
      // says otherwise. This is the admin's intent for this save; it is not
      // stored on the partner.
      submitData.useCustomRates = useCustomRates;

      if (isEditMode) {
        // In edit mode: only send password if admin is explicitly resetting it
        if (!submitData.password) delete submitData.password;

        await axios.put(`${API_URL}/api/3pl-partners/${id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Partner updated successfully!');
      } else {
        // Create mode: never send a password — invite flow handles it
        delete submitData.password;
        delete submitData.confirmPassword;

        const response = await axios.post(`${API_URL}/api/3pl-partners`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const invite = response.data?.data?.invite;
        if (invite?.inviteEmailSent) {
          setSuccess(`Partner created! An activation email has been sent to ${invite.email}.`);
        } else {
          setSuccess('Partner created! Email delivery failed — share the activation link manually from the partner details page.');
        }
      }

      setTimeout(() => {
        history.push('/admin/3pl-partners');
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.error?.message || `Failed to ${isEditMode ? 'update' : 'create'} partner`);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────

  return (
    <div className="content" style={{ fontFamily: font.sans, maxWidth: 880, margin: '0 auto', padding: '0 4px' }}>
      {fetchingData ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
          <Spinner animation="grow" style={{ color: colors.green }} />
        </div>
      ) : (
        <>
          {/* ── Page header ──────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 0 18px', borderBottom: '1px solid #E5E7EB', marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#111', letterSpacing: '-0.3px' }}>
                {isEditMode ? 'Edit Partner' : 'New Partner'}
              </h1>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6B7280' }}>
                {isEditMode ? 'Update partner information and settings' : 'Add a new 3PL logistics partner'}
              </p>
            </div>
            <button type="button" onClick={() => history.goBack()} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
              <FaArrowLeft style={{ fontSize: 12 }} /> Back
            </button>
          </div>

          {/* ── Alerts ──────────────────────────────────────── */}
          {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 16px', color: '#B91C1C', fontSize: 13, marginBottom: 16 }}>{error}</div>}
          {success && <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '12px 16px', color: '#166534', fontSize: 13, marginBottom: 16 }}>{success}</div>}

          <Form onSubmit={handleSubmit}>

            {/* ══ LOGO (edit only) ══════════════════════════ */}
            {isEditMode && (
              <div style={card}>
                <div style={{ ...cardBody, display: 'flex', alignItems: 'center', gap: 20 }}>
                  <div onClick={!uploadingLogo ? handleLogoUpload : undefined} title="Click to change logo"
                    style={{ width: 72, height: 72, borderRadius: '50%', flexShrink: 0, background: currentLogo?.url ? 'transparent' : '#023485', border: `2.5px solid ${currentLogo?.url ? '#006202' : '#023485'}`, overflow: 'hidden', cursor: uploadingLogo ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    {uploadingLogo ? <Spinner size="sm" animation="border" style={{ color: '#fff' }} /> : currentLogo?.url ? <img src={currentLogo.url} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <FaCamera style={{ color: '#fff', fontSize: 22 }} />}
                    {!uploadingLogo && <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }} onMouseEnter={e => e.currentTarget.style.opacity = 1} onMouseLeave={e => e.currentTarget.style.opacity = 0}><FaCamera style={{ color: '#fff', fontSize: 18 }} /></div>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 2px', fontWeight: 600, fontSize: 14, color: '#111' }}>{formData.companyName || 'Partner'} Logo</p>
                    <p style={{ margin: '0 0 10px', fontSize: 12, color: '#9CA3AF' }}>Square · JPG / PNG / WebP · Max 500 KB · {currentLogo?.url ? <span style={{ color: '#006202', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 3 }}><FaCheck style={{ fontSize: 10 }} /> Uploaded</span> : 'No logo yet'}</p>
                    <button type="button" onClick={handleLogoUpload} disabled={uploadingLogo} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid #023485', background: '#023485', color: '#fff', fontSize: 12, fontWeight: 500, cursor: uploadingLogo ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <FaCamera style={{ fontSize: 11 }} />{uploadingLogo ? 'Saving…' : currentLogo?.url ? 'Replace Logo' : 'Upload Logo'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ══ COMPANY INFORMATION ═══════════════════════════════════════ */}
            <div style={card}>
              <div style={cardBody}>
                <p style={sectionTitle}>Company Information</p>
                <div style={grid(3)}>
                  <Field label="Company Name" required>
                    <input name="companyName" value={formData.companyName} onChange={handleChange} required placeholder="Acme Logistics" {...inp()} />
                  </Field>
                  <Field label="Registration No.">
                    <input name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} placeholder="Business reg." {...inp()} />
                  </Field>
                  <Field label="Tax ID (TIN)">
                    <input name="taxId" value={formData.taxId} onChange={handleChange} placeholder="TIN number" {...inp()} />
                  </Field>
                </div>
              </div>
            </div>

            {/* ══ CONTACT PERSON ════════════════════════════════════════════ */}
            <div style={card}>
              <div style={cardBody}>
                <p style={sectionTitle}>Contact Person</p>
                <div style={{ ...grid(2), marginBottom: 16 }}>
                  <Field label="Full Name" required>
                    <input name="contactPerson.name" value={formData.contactPerson.name} onChange={handleChange} required placeholder="Jane Doe" {...inp()} />
                  </Field>
                  <Field label="Position">
                    <input name="contactPerson.position" value={formData.contactPerson.position} onChange={handleChange} placeholder="Operations Manager" {...inp()} />
                  </Field>
                </div>
                <div style={grid(2)}>
                  <Field label="Phone" required>
                    <input type="tel" name="contactPerson.phone" value={formData.contactPerson.phone} onChange={handleChange} required placeholder="+256 …" {...inp()} />
                  </Field>
                  <Field label="Contact Email" required help="For communications — not used to log in">
                    <input type="email" name="contactPerson.email" value={formData.contactPerson.email} onChange={handleChange} required placeholder="ops@company.com" {...inp()} />
                  </Field>
                </div>
              </div>
            </div>

            {/* ══ ADDRESS ═══════════════════════════════════════════════════ */}
            <div style={card}>
              <div style={cardBody}>
                <p style={sectionTitle}>Business Address</p>
                <div style={grid(3)}>
                  <Field label="Street">
                    <input name="address.street" value={formData.address.street} onChange={handleChange} placeholder="123 Main St" {...inp()} />
                  </Field>
                  <Field label="City">
                    <input name="address.city" value={formData.address.city} onChange={handleChange} placeholder="Kampala" {...inp()} />
                  </Field>
                  <Field label="District">
                    <input name="address.district" value={formData.address.district} onChange={handleChange} placeholder="Kampala" {...inp()} />
                  </Field>
                </div>
              </div>
            </div>

            {/* ══ PARTNERSHIP ═══════════════════════════════════════════════ */}
            <div style={card}>
              <div style={cardBody}>
                <p style={sectionTitle}>Partnership &amp; Commission</p>
                <div style={{ display: 'grid', gridTemplateColumns: isEditMode ? 'repeat(6, 1fr)' : 'repeat(5, 1fr)', gap: '0 14px' }}>
                  {isEditMode && (
                    <Field label="Status">
                      <AppSelect name="status" value={formData.status} onChange={handleChange} {...sel()} style={{ ...inputBase, color: formData.status === 'active' ? '#006202' : formData.status === 'suspended' ? '#EF4444' : '#374151', fontWeight: 600 }}>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="inactive">Inactive</option>
                      </AppSelect>
                    </Field>
                  )}
                  <Field label="Tier">
                    <AppSelect name="partnershipTier" value={formData.partnershipTier} onChange={handleChange} {...sel()}>
                      <option value="bronze">Bronze</option>
                      <option value="silver">Silver</option>
                      <option value="gold">Gold</option>
                      <option value="platinum">Platinum</option>
                    </AppSelect>
                  </Field>
                  {[{ label: 'Lusuku %', name: 'commissionRate' }, { label: 'Partner %', name: 'partnerShareRate' }, { label: 'Rider %', name: 'riderShareRate' }, { label: 'Base fee (UGX, not used in pricing)', name: 'baseDeliveryFee' }].map(({ label, name }) => (
                    <Field key={name} label={label}>
                      <input
                        name={name}
                        type="number"
                        min={0}
                        value={formData[name]}
                        onChange={handleChange}
                        disabled={!useCustomRates}
                        style={useCustomRates ? inputBase : disabledInput}
                        {...(useCustomRates ? { onFocus, onBlur } : {})}
                      />
                    </Field>
                  ))}
                </div>
                {(() => {
                  const splitSum = Number(formData.commissionRate) + Number(formData.partnerShareRate) + Number(formData.riderShareRate);
                  const splitOk = Math.abs(splitSum - 100) < 0.01;
                  return (
                    <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontSize: 12.5, color: '#374151' }}>
                        <input
                          type="checkbox"
                          checked={useCustomRates}
                          onChange={(e) => {
                            setUseCustomRates(e.target.checked);
                            if (!e.target.checked && tierConfig) {
                              const tier = tierConfig[formData.partnershipTier] || tierConfig.bronze;
                              if (tier) setFormData(prev => ({ ...prev, commissionRate: tier.commissionRate, partnerShareRate: tier.partnerShareRate, riderShareRate: tier.riderShareRate, baseDeliveryFee: tier.baseDeliveryFee }));
                            }
                          }}
                          style={{ width: 15, height: 15, cursor: 'pointer' }}
                        />
                        Override tier defaults with custom rates
                      </label>
                      {useCustomRates && (
                        <span style={{
                          fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 6,
                          background: splitOk ? '#D1FAE5' : '#FEF3C7',
                          color: splitOk ? '#065F46' : '#92400E',
                          border: `1px solid ${splitOk ? '#6EE7B7' : '#FDE68A'}`,
                        }}>
                          {splitOk ? `✓ Splits sum to 100%` : `⚠ Splits sum to ${splitSum.toFixed(1)}% — must equal 100`}
                        </span>
                      )}
                      {!useCustomRates && (
                        <span style={{ fontSize: 11, color: colors.faint }}>
                          Rates set automatically by selected tier.
                        </span>
                      )}
                      {tierConfigError && (
                        <p style={{ margin: '6px 0 0', fontSize: 11.5, color: colors.amber, flexBasis: '100%' }}>
                          {tierConfigError}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* ══ BANK DETAILS ══════════════════════════════════════════════ */}
            <div style={card}>
              <div style={cardBody}>
                <p style={sectionTitle}>Bank Details <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: 'none' }}>— for payouts</span></p>
                <div style={{ ...grid(2), marginBottom: 16 }}>
                  <Field label="Bank Name">
                    <input name="bankDetails.bankName" value={formData.bankDetails.bankName} onChange={handleChange} placeholder="e.g. Stanbic Bank" {...inp()} />
                  </Field>
                  <Field label="Account Name">
                    <input name="bankDetails.accountName" value={formData.bankDetails.accountName} onChange={handleChange} placeholder="Account holder" {...inp()} />
                  </Field>
                </div>
                <div style={grid(2)}>
                  <Field label="Account Number">
                    <input name="bankDetails.accountNumber" value={formData.bankDetails.accountNumber} onChange={handleChange} placeholder="Account number" {...inp()} />
                  </Field>
                  <Field label="Branch Code">
                    <input name="bankDetails.branchCode" value={formData.bankDetails.branchCode} onChange={handleChange} placeholder="Branch code" {...inp()} />
                  </Field>
                </div>
              </div>
            </div>

            {/* ══ DASHBOARD ACCESS ══════════════════════════════════════════ */}
            <div style={card}>
              <div style={cardBody}>
                <p style={sectionTitle}>Dashboard Access</p>
                {!isEditMode ? (
                  <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '11px 14px', fontSize: 12.5, color: '#1E40AF', marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <FaEnvelope style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }} />
                    <span><strong>Secure invite flow —</strong> an activation link will be emailed to the address below. The partner sets their own password — no plain-text credentials are shared.</span>
                  </div>
                ) : (
                  <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '11px 14px', fontSize: 12.5, color: '#92400E', marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <FaUserShield style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }} />
                    <span><strong>Admin —</strong> update the login email or reset the password. Leave password fields empty to keep the existing one.</span>
                  </div>
                )}
                <div style={isEditMode ? grid(3) : { maxWidth: 360 }}>
                  <Field label={`Login Email${!isEditMode ? ' *' : ''}`} help={isEditMode ? 'Used to sign in to the dashboard' : 'Activation link will be sent here'}>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="partner@company.com" required={!isEditMode} {...inp()} />
                  </Field>
                  {isEditMode && (
                    <>
                      <Field label="New Password" help="8+ chars · 1 uppercase · 1 number">
                        <PasswordInput name="password" value={formData.password} onChange={handleChange} placeholder="Leave empty to keep current" autoComplete="new-password" {...inp()} />
                      </Field>
                      <Field label="Confirm Password">
                        <PasswordInput name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder="Re-enter new password" autoComplete="new-password" {...inp()} />
                      </Field>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ══ ACTIONS ═══════════════════════════════════════════════════ */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '4px 0 32px' }}>
              <button type="button" onClick={() => history.goBack()} style={{ padding: '9px 20px', borderRadius: 9, border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="submit" disabled={loading} style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: loading ? '#9CA3AF' : '#006202', color: '#fff', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                {loading
                  ? <><Spinner size="sm" animation="border" />{isEditMode ? 'Saving…' : 'Creating…'}</>
                  : isEditMode
                    ? <><FaCheck style={{ fontSize: 11 }} /> Save Changes</>
                    : <><FaPlus style={{ fontSize: 11 }} /> Create Partner</>
                }
              </button>
            </div>
          </Form>
        </>
      )}
    </div>
  );
};

export default CreatePartner;
